import { Controller } from "@hotwired/stimulus"

// Modern OTP input with individual digit boxes
// Handles auto-advance, backspace navigation, and paste
export default class extends Controller {
  static targets = ["digit", "hidden"]
  static values = { length: { type: Number, default: 6 } }

  connect() {
    // Focus first input on connect
    this.digitTargets[0]?.focus()
  }

  handleInput(event) {
    const input = event.target
    const index = this.digitTargets.indexOf(input)
    const value = input.value

    // Only allow numbers
    const digits = value.replace(/\D/g, '')

    // Handle iOS autofill: when multiple digits are entered at once
    // (e.g., tapping "From Messages: 123456" suggestion), distribute across fields
    if (digits.length > 1) {
      this.fillDigits(digits, index)
      return
    }

    // Single digit input
    input.value = digits

    // Auto-advance to next input
    if (input.value && index < this.digitTargets.length - 1) {
      this.digitTargets[index + 1].focus()
    }

    this.updateHiddenField()
  }

  // Fill digits starting from a given index (used by autofill and paste)
  fillDigits(digits, startIndex = 0) {
    const chars = digits.slice(0, this.lengthValue - startIndex).split('')

    chars.forEach((digit, i) => {
      const targetIndex = startIndex + i
      if (this.digitTargets[targetIndex]) {
        this.digitTargets[targetIndex].value = digit
      }
    })

    // If all fields are filled, blur to remove selection highlight
    // Otherwise focus the next empty field
    const nextEmptyIndex = startIndex + chars.length
    if (nextEmptyIndex >= this.lengthValue) {
      document.activeElement?.blur()
    } else {
      this.digitTargets[nextEmptyIndex]?.focus()
    }

    this.updateHiddenField()
  }

  handleKeydown(event) {
    const input = event.target
    const index = this.digitTargets.indexOf(input)

    // Handle backspace - go to previous input
    if (event.key === 'Backspace' && !input.value && index > 0) {
      this.digitTargets[index - 1].focus()
      this.digitTargets[index - 1].value = ''
      this.updateHiddenField()
    }

    // Handle left arrow
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      this.digitTargets[index - 1].focus()
    }

    // Handle right arrow
    if (event.key === 'ArrowRight' && index < this.digitTargets.length - 1) {
      event.preventDefault()
      this.digitTargets[index + 1].focus()
    }
  }

  handlePaste(event) {
    event.preventDefault()
    const pastedData = (event.clipboardData || window.clipboardData).getData('text')
    const digits = pastedData.replace(/\D/g, '')
    this.fillDigits(digits, 0)
  }

  handleFocus(event) {
    // Select content on focus for easy replacement
    event.target.select()
  }

  updateHiddenField() {
    const code = this.digitTargets.map(input => input.value).join('')
    this.hiddenTarget.value = code
  }

  // Allow form submission when all digits are filled
  get isComplete() {
    return this.digitTargets.every(input => input.value.length === 1)
  }
}
